import { useEffect, useRef, useState } from 'react'

import { useNavigate } from '@tanstack/react-router'
import { ChevronDown, FileText, MoreVertical, Search, User } from 'lucide-react'

import { cn } from '@/lib/utils'

import { useSiteDetail } from '../../hooks/useSiteDetail'

import type { SiteSurveyItem } from '../../api/siteApi'

// ─── Status config ────────────────────────────────────────────────────────

type SurveyStatus = SiteSurveyItem['status']

interface StatusTone {
  label: string
  accent: string
  tilePill: string
  tileFg: string
  pill: string
}

const STATUS_TONE: Record<SurveyStatus, StatusTone> = {
  pending: {
    label: 'Pending',
    accent: 'bg-warning',
    tilePill: 'from-[#fef9ec] to-warning-soft',
    tileFg: 'text-warning',
    pill: 'bg-warning-soft text-warning',
  },
  in_progress: {
    label: 'In Progress',
    accent: 'bg-brand',
    tilePill: 'from-brand-tint to-brand-soft',
    tileFg: 'text-brand',
    pill: 'bg-brand-soft text-brand',
  },
  completed: {
    label: 'Completed',
    accent: 'bg-success',
    tilePill: 'from-[#f0faf4] to-success-soft',
    tileFg: 'text-success',
    pill: 'bg-success-soft text-success',
  },
  missed: {
    label: 'Missed',
    accent: 'bg-danger',
    tilePill: 'from-[#fdf3f1] to-danger-soft',
    tileFg: 'text-danger',
    pill: 'bg-danger-soft text-danger',
  },
}

// ─── Date tile ────────────────────────────────────────────────────────────

function DateTile({ dateStr, tone }: { dateStr: string | null | undefined; tone: StatusTone }) {
  if (!dateStr) {
    return (
      <div
        className={cn(
          'flex size-11 shrink-0 flex-col items-center justify-center rounded-[9px] bg-gradient-to-br leading-none',
          tone.tilePill,
          tone.tileFg,
        )}
      >
        <span className="text-[20px] font-extrabold opacity-50">—</span>
      </div>
    )
  }
  const d = new Date(dateStr)
  const day = d.toLocaleDateString('en-GB', { day: '2-digit' })
  const mon = d.toLocaleDateString('en-GB', { month: 'short' })
  return (
    <div
      className={cn(
        'flex size-11 shrink-0 flex-col items-center justify-center rounded-[9px] bg-gradient-to-br leading-none',
        tone.tilePill,
        tone.tileFg,
      )}
    >
      <span className="text-[15px] font-extrabold tracking-[-0.02em] tabular-nums">{day}</span>
      <span className="mt-0.5 text-[9px] font-bold tracking-[0.08em] uppercase opacity-85">
        {mon}
      </span>
    </div>
  )
}

// ─── Survey row ───────────────────────────────────────────────────────────

function SurveyRow({ survey, entityId }: { survey: SiteSurveyItem; entityId: string }) {
  const navigate = useNavigate()
  const tone = STATUS_TONE[survey.status] ?? STATUS_TONE.pending
  const officerName =
    survey.assigned_officer?.display_name ??
    survey.officer_name ??
    survey.officer?.name ??
    ''
  const dateStr = survey.survey_date ?? survey.completed_at ?? survey.due_date
  const hasIssues = (survey.issues_count ?? 0) > 0

  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', onOutside)
    }
    return () => document.removeEventListener('mousedown', onOutside)
  }, [menuOpen])

  function handleViewResponse() {
    if (!entityId) {
      return
    }
    navigate({
      to: '/entities/$id/response-details',
      params: { id: entityId },
      search: { taskId: survey.id },
    })
  }

  return (
    <article className="border-line relative isolate flex items-center gap-3 overflow-hidden rounded-[11px] border bg-white px-3.5 py-[11px] transition-all duration-150 hover:-translate-y-px hover:border-[#dde3f0] hover:shadow-[0_10px_24px_-14px_rgba(11,21,48,0.14)]">
      <span
        aria-hidden="true"
        className={cn('absolute top-0 bottom-0 left-0 w-[3px] opacity-85', tone.accent)}
      />

      <DateTile dateStr={dateStr} tone={tone} />

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-ink text-[13px] font-bold tabular-nums">{survey.survey_ref}</span>
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-[2px] text-[10.5px] font-bold tracking-[0.04em] uppercase',
              tone.pill,
            )}
          >
            <span className="size-1 rounded-full bg-current" />
            {tone.label}
          </span>
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-[2px] text-[10.5px] font-bold',
              hasIssues ? 'bg-danger-soft text-danger' : 'bg-success-soft text-success',
            )}
          >
            {hasIssues
              ? `${survey.issues_count} ${survey.issues_count === 1 ? 'issue' : 'issues'}`
              : 'No issues'}
          </span>
        </div>
        {officerName && (
          <div className="text-ink-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
            <span className="inline-flex items-center gap-1">
              <User size={11} strokeWidth={2} className="text-muted-text shrink-0" />
              {officerName}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-1.5">
        {survey.status === 'completed' && (
          <button
            type="button"
            onClick={handleViewResponse}
            className="border-line text-ink-2 hover:border-brand hover:text-brand hover:bg-brand-tint inline-flex h-[30px] items-center gap-1.5 rounded-full border bg-white px-3.5 text-[12px] font-semibold transition-colors"
          >
            <FileText size={11} strokeWidth={2} />
            View Form Response
          </button>
        )}
        <div ref={menuRef} className="relative">
          <button
            type="button"
            aria-label="More options"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            className="text-muted-text hover:bg-line-2 hover:text-ink grid size-[30px] place-items-center rounded-[8px] transition-colors"
          >
            <MoreVertical size={13} strokeWidth={2} />
          </button>
          {menuOpen && (
            <div
              role="menu"
              className="border-line/70 absolute top-full right-0 z-30 mt-1.5 w-[200px] overflow-hidden rounded-[14px] border bg-white p-1 shadow-[0_18px_36px_-14px_rgba(11,21,48,0.30),0_4px_10px_-4px_rgba(11,21,48,0.10)]"
            >
              <button
                type="button"
                role="menuitem"
                disabled={survey.status !== 'completed'}
                onClick={() => {
                  setMenuOpen(false)
                  handleViewResponse()
                }}
                className="text-ink-2 hover:bg-brand-tint hover:text-brand flex w-full items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-left text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FileText size={15} strokeWidth={1.9} />
                View response
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  )
}

// ─── Tab ──────────────────────────────────────────────────────────────────

type IssuesFilter = '' | 'with' | 'without'

const ISSUES_FILTER_OPTIONS: { value: IssuesFilter; label: string }[] = [
  { value: '', label: 'All surveys' },
  { value: 'with', label: 'With issues' },
  { value: 'without', label: 'Without issues' },
]

export function SiteSurveysTab({ siteId }: { siteId: string }) {
  const [search, setSearch] = useState('')
  const [issuesFilter, setIssuesFilter] = useState<IssuesFilter>('')

  const { data: site, isLoading } = useSiteDetail(siteId)
  const surveys = site?.surveys?.items ?? []
  const entityId = site?.entity_id ?? ''

  const filtered = surveys.filter((s) => {
    if (search.trim() && !s.survey_ref?.toLowerCase().includes(search.toLowerCase())) {
      return false
    }
    if (issuesFilter === 'with' && (s.issues_count ?? 0) === 0) {
      return false
    }
    if (issuesFilter === 'without' && (s.issues_count ?? 0) > 0) {
      return false
    }
    return true
  })

  return (
    <div className="flex flex-col">
      {/* Toolbar */}
      <div className="border-line-2 flex flex-wrap items-center gap-2.5 border-b px-3.5 py-3">
        <div className="focus-within:border-brand-3 focus-within:bg-white focus-within:shadow-[0_0_0_3px_rgba(22,93,255,0.10)] inline-flex h-8 w-[240px] shrink-0 items-center gap-1.5 rounded-[8px] border border-transparent bg-line-2 px-3 transition-colors">
          <Search size={13} className="text-muted-text shrink-0" />
          <input
            type="text"
            placeholder="Search surveys"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="text-ink placeholder:text-muted-text-2 h-full flex-1 border-0 bg-transparent text-[13px] outline-none"
          />
        </div>
        <div className="relative w-[160px] shrink-0">
          <select
            value={issuesFilter}
            onChange={(e) => setIssuesFilter(e.target.value as IssuesFilter)}
            className="border-line text-ink-2 hover:border-[#cdd6e8] focus:border-brand-3 font-display h-8 w-full cursor-pointer appearance-none rounded-[7px] border bg-white py-0 pr-7 pl-2.5 text-[13px] font-medium outline-none focus:shadow-[0_0_0_3px_rgba(22,93,255,0.10)]"
          >
            {ISSUES_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={11}
            className="text-muted-text pointer-events-none absolute top-1/2 right-2 -translate-y-1/2"
          />
        </div>
      </div>

      {/* Rows */}
      <div className="flex flex-col gap-2 p-3.5">
        {isLoading && (
          <div className="text-muted-text py-10 text-center text-[13px] font-medium">
            Loading surveys…
          </div>
        )}
        {!isLoading && filtered.length === 0 && (
          <div className="text-muted-text py-10 text-center text-[13px] font-medium">
            {search.trim() || issuesFilter
              ? 'No surveys match your filters.'
              : 'No surveys for this site yet.'}
          </div>
        )}
        {filtered.map((survey) => (
          <SurveyRow key={survey.id} survey={survey} entityId={entityId} />
        ))}
      </div>
    </div>
  )
}
